/**
 * Pre-Flight Validator
 * Validates all prerequisites before simulation can start
 * Uses corrected API routes from v6.0 specification
 */

import { SimulatorRateLimiter, getSharedRateLimiter } from './RateLimiter';
import { AuthService } from '../../../../../services/authService';
import {
  PreFlightValidation,
  PreFlightReport,
  ValidationCategory,
  ValidationStatus
} from '../types';

// API base URL - use environment variable for production deployments
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

// Authenticated fetch helpers using AuthService
const authenticatedGet = async (url: string): Promise<any> => {
  const response = await AuthService.authenticatedGet(url);

  if (!response.ok) {
    throw new Error(`Request failed: ${response.statusText}`);
  }

  return response.json();
};

const authenticatedPost = async (url: string, body?: object): Promise<any> => {
  const response = await AuthService.authenticatedPost(url, body);

  if (!response.ok) {
    throw new Error(`Request failed: ${response.statusText}`);
  }

  return response.json();
};

export class PreFlightValidator {
  private readonly companyNo: string;
  private readonly rateLimiter: SimulatorRateLimiter;

  constructor(companyNo: string) {
    this.companyNo = companyNo;
    this.rateLimiter = getSharedRateLimiter();
  }

  /**
   * Run full pre-flight validation
   */
  async runFullValidation(): Promise<PreFlightReport> {
    const validations: PreFlightValidation[] = [];

    // KRITISKE SJEKKER (blokkerer alltid)
    await this.rateLimiter.throttle();
    validations.push(await this.checkSystemSetup());

    await this.rateLimiter.throttle();
    validations.push(await this.checkRuleGroupExists());

    await this.rateLimiter.throttle();
    validations.push(await this.checkWarehouseExists());

    // PÅKREVDE SJEKKER
    await this.rateLimiter.throttle();
    validations.push(await this.checkBinsExist());

    await this.rateLimiter.throttle();
    validations.push(await this.checkItemsExist());

    await this.rateLimiter.throttle();
    validations.push(await this.checkOperatorsExist());

    // WORKFLOW-SPESIFIKKE SJEKKER
    await this.rateLimiter.throttle();
    validations.push(await this.checkItemBinsHaveStock());

    await this.rateLimiter.throttle();
    validations.push(await this.checkOrdersAvailable());

    await this.rateLimiter.throttle();
    validations.push(await this.checkPurchaseOrdersAvailable());

    await this.rateLimiter.throttle();
    validations.push(await this.checkPickingListsAvailable());

    // KAPASITET
    await this.rateLimiter.throttle();
    validations.push(await this.checkNoStaleLocks());

    // BATCH/SERIAL
    await this.rateLimiter.throttle();
    validations.push(await this.checkBatchSerialItems());

    return this.generateReport(validations);
  }

  private async checkSystemSetup(): Promise<PreFlightValidation> {
    try {
      // KORRIGERT RUTE (v6.0)
      const setup = await authenticatedGet(`${API_BASE_URL}/system-setup/current`);

      return {
        category: 'KRITISK',
        name: 'SystemSetup konfigurert',
        status: setup ? 'pass' : 'fail',
        message: setup ? `Bedrift: ${setup.companyName || setup.CompanyName || 'OK'}` : 'SystemSetup mangler!',
        canProceed: !!setup,
        canAutoFix: false
      };
    } catch (error) {
      return {
        category: 'KRITISK',
        name: 'SystemSetup konfigurert',
        status: 'fail',
        message: 'Kunne ikke hente SystemSetup - databasen er ikke korrekt konfigurert',
        canProceed: false,
        canAutoFix: false
      };
    }
  }

  private async checkRuleGroupExists(): Promise<PreFlightValidation> {
    try {
      const ruleGroups = await authenticatedGet(`${API_BASE_URL}/${this.companyNo}/rulegroupengine/rulegroups`);
      const ruleGroupList = ruleGroups || [];
      const activeGroups = ruleGroupList.filter((rg: any) => rg.activeYesNo || rg.ActiveYesNo);

      return {
        category: 'KRITISK',
        name: 'Regelgruppe for PLister',
        status: activeGroups.length > 0 ? 'pass' : 'fail',
        message: activeGroups.length > 0
          ? `${activeGroups.length} aktiv(e) regelgruppe(r) funnet`
          : 'Ingen aktiv regelgruppe - kan ikke opprette PLister!',
        canProceed: activeGroups.length > 0,
        canAutoFix: false
      };
    } catch (error) {
      return {
        category: 'KRITISK',
        name: 'Regelgruppe for PLister',
        status: 'fail',
        message: 'Kunne ikke hente regelgrupper',
        canProceed: false,
        canAutoFix: false
      };
    }
  }

  private async checkWarehouseExists(): Promise<PreFlightValidation> {
    try {
      // KORRIGERT RUTE (v6.0) - entall, ikke flertall
      const warehouses = await authenticatedGet(`${API_BASE_URL}/${this.companyNo}/warehouse`);
      const warehouseList = Array.isArray(warehouses) ? warehouses : [warehouses].filter(Boolean);

      return {
        category: 'KRITISK',
        name: 'Lager definert',
        status: warehouseList.length > 0 ? 'pass' : 'fail',
        message: warehouseList.length > 0
          ? `${warehouseList.length} lager funnet: ${warehouseList.map((w: any) => w.warehouseID || w.WarehouseID).join(', ')}`
          : 'Ingen lager definert!',
        canProceed: warehouseList.length > 0,
        canAutoFix: false
      };
    } catch (error) {
      return {
        category: 'KRITISK',
        name: 'Lager definert',
        status: 'fail',
        message: 'Kunne ikke hente lagerliste',
        canProceed: false,
        canAutoFix: false
      };
    }
  }

  private async checkBinsExist(): Promise<PreFlightValidation> {
    try {
      // Korrekt endepunkt: /data-inspection/stcbins
      const bins = await authenticatedGet(`${API_BASE_URL}/data-inspection/stcbins`);
      const binList = bins || [];
      const count = Array.isArray(binList) ? binList.length : 0;
      const minRequired = 10;

      return {
        category: 'PÅKREVD',
        name: 'Hyller/lokasjoner registrert',
        status: count >= minRequired ? 'pass' : count > 0 ? 'warning' : 'fail',
        message: `${count} hylle(r) funnet (minimum ${minRequired} anbefalt)`,
        canProceed: count > 0,
        canAutoFix: false
      };
    } catch (error) {
      return {
        category: 'PÅKREVD',
        name: 'Hyller/lokasjoner registrert',
        status: 'fail',
        message: 'Kunne ikke hente hyller',
        canProceed: false,
        canAutoFix: false
      };
    }
  }

  private async checkItemsExist(): Promise<PreFlightValidation> {
    try {
      // KORRIGERT RUTE (v6.0)
      const items = await authenticatedGet(`${API_BASE_URL}/items/current?take=100`);
      const itemList = items || [];
      const count = Array.isArray(itemList) ? itemList.length : 0;
      const minRequired = 10;

      return {
        category: 'PÅKREVD',
        name: 'Varer registrert',
        status: count >= minRequired ? 'pass' : count > 0 ? 'warning' : 'fail',
        message: `${count} vare(r) funnet (minimum ${minRequired} anbefalt)`,
        canProceed: count > 0,
        canAutoFix: false
      };
    } catch (error) {
      return {
        category: 'PÅKREVD',
        name: 'Varer registrert',
        status: 'fail',
        message: 'Kunne ikke hente varer',
        canProceed: false,
        canAutoFix: false
      };
    }
  }

  private async checkOperatorsExist(): Promise<PreFlightValidation> {
    try {
      // Sjekk at innlogget bruker eksisterer via /user-profiles/current
      const currentUser = await authenticatedGet(`${API_BASE_URL}/user-profiles/current`);

      return {
        category: 'PÅKREVD',
        name: 'Operatører registrert',
        status: currentUser ? 'pass' : 'warning',
        message: currentUser ? `Innlogget som: ${currentUser.userID || currentUser.userId || 'OK'}` : 'Kunne ikke verifisere bruker',
        canProceed: true, // Always allow - we're already logged in
        canAutoFix: false
      };
    } catch (error) {
      return {
        category: 'PÅKREVD',
        name: 'Operatører registrert',
        status: 'warning',
        message: 'Kunne ikke sjekke operatører (fortsetter uansett)',
        canProceed: true, // Can proceed anyway - we're already authenticated
        canAutoFix: false
      };
    }
  }

  private async checkItemBinsHaveStock(): Promise<PreFlightValidation> {
    try {
      // KORRIGERT RUTE (v6.0)
      const itemBins = await authenticatedGet(`${API_BASE_URL}/data-inspection/itembins`);
      const itemBinList = itemBins || [];
      const withStock = itemBinList.filter((ib: any) => (ib.quantity || ib.Quantity || 0) > 0);

      const hasAutoFix = withStock.length === 0;

      return {
        category: 'ANBEFALT',
        name: 'Lagerbeholdning',
        status: withStock.length >= 10 ? 'pass' : withStock.length > 0 ? 'warning' : 'info',
        message: withStock.length > 0
          ? `${withStock.length} posisjoner med beholdning`
          : 'Ingen beholdning - bruk Receiving + Put-Away workflow for å generere',
        canProceed: true,
        canAutoFix: hasAutoFix,
        fixAction: hasAutoFix ? () => this.generateStockViaReceiving() : undefined
      };
    } catch (error) {
      return {
        category: 'ANBEFALT',
        name: 'Lagerbeholdning',
        status: 'warning',
        message: 'Kunne ikke sjekke beholdning',
        canProceed: true,
        canAutoFix: false
      };
    }
  }

  private async checkOrdersAvailable(): Promise<PreFlightValidation> {
    try {
      // KORRIGERT RUTE (v6.0)
      const orders = await authenticatedGet(`${API_BASE_URL}/orders?status=0&take=100`);
      const orderList = orders || [];
      const count = Array.isArray(orderList) ? orderList.length : 0;

      const hasAutoFix = count === 0;

      return {
        category: 'VALGFRITT',
        name: 'Salgsordrer tilgjengelig',
        status: count > 0 ? 'pass' : 'info',
        message: count > 0
          ? `${count} ubehandlede ordre(r)`
          : 'Ingen ordrer - kan genereres automatisk',
        canProceed: true,
        canAutoFix: hasAutoFix,
        fixAction: hasAutoFix ? () => this.generateTestOrders() : undefined
      };
    } catch (error) {
      return {
        category: 'VALGFRITT',
        name: 'Salgsordrer tilgjengelig',
        status: 'warning',
        message: 'Kunne ikke sjekke ordrer',
        canProceed: true,
        canAutoFix: false
      };
    }
  }

  private async checkPurchaseOrdersAvailable(): Promise<PreFlightValidation> {
    try {
      const orders = await authenticatedGet(`${API_BASE_URL}/porders/receiving`);
      const orderList = orders || [];
      const count = Array.isArray(orderList) ? orderList.length : 0;

      return {
        category: 'VALGFRITT',
        name: 'Innkjøpsordrer tilgjengelig',
        status: count > 0 ? 'pass' : 'info',
        message: count > 0
          ? `${count} innkjøpsordre(r) klar for mottak`
          : 'Ingen innkjøpsordrer - kan genereres automatisk',
        canProceed: true,
        canAutoFix: count === 0,
        fixAction: count === 0 ? () => this.generatePurchaseOrders() : undefined
      };
    } catch (error) {
      return {
        category: 'VALGFRITT',
        name: 'Innkjøpsordrer tilgjengelig',
        status: 'warning',
        message: 'Kunne ikke sjekke innkjøpsordrer',
        canProceed: true,
        canAutoFix: false
      };
    }
  }

  private async checkPickingListsAvailable(): Promise<PreFlightValidation> {
    try {
      const lists = await authenticatedGet(`${API_BASE_URL}/picking/available`);
      const listArray = lists || [];
      const count = Array.isArray(listArray) ? listArray.length : 0;

      return {
        category: 'VALGFRITT',
        name: 'Plukklister tilgjengelig',
        status: count > 0 ? 'pass' : 'info',
        message: count > 0
          ? `${count} plukkliste(r) klar for plukking`
          : 'Ingen PLister - opprettes automatisk fra ordrer via RuleEngine',
        canProceed: true,
        canAutoFix: count === 0,
        fixAction: count === 0 ? () => this.createPickingListsViaRuleEngine() : undefined
      };
    } catch (error) {
      return {
        category: 'VALGFRITT',
        name: 'Plukklister tilgjengelig',
        status: 'warning',
        message: 'Kunne ikke sjekke plukklister',
        canProceed: true,
        canAutoFix: false
      };
    }
  }

  private async checkNoStaleLocks(): Promise<PreFlightValidation> {
    try {
      const lockedLists = await authenticatedGet(`${API_BASE_URL}/picking/lists/by-status?status=20`);
      const lockedListArray = lockedLists || [];
      const staleLocks = lockedListArray.filter((l: any) => {
        const lockedBy = l.lockedBy || l.LockedBy;
        return lockedBy && lockedBy !== '';
      });

      return {
        category: 'ANBEFALT',
        name: 'Ingen låste plukklister',
        status: staleLocks.length === 0 ? 'pass' : 'warning',
        message: staleLocks.length === 0
          ? 'Ingen låste lister'
          : `${staleLocks.length} låst(e) liste(r) - anbefales frigjort før simulering`,
        canProceed: true,
        canAutoFix: staleLocks.length > 0,
        fixAction: staleLocks.length > 0 ? () => this.releaseAllLocks(staleLocks) : undefined
      };
    } catch (error) {
      return {
        category: 'ANBEFALT',
        name: 'Ingen låste plukklister',
        status: 'info',
        message: 'Kunne ikke sjekke låser',
        canProceed: true,
        canAutoFix: false
      };
    }
  }

  private async checkBatchSerialItems(): Promise<PreFlightValidation> {
    try {
      const items = await authenticatedGet(`${API_BASE_URL}/items/current?take=500`);
      const itemList = items || [];
      const batchItems = itemList.filter((i: any) => {
        const itemType = i.itemType ?? i.ItemType ?? 0;
        return itemType === 4 || itemType === 5;
      });
      const serialItems = itemList.filter((i: any) => {
        const itemType = i.itemType ?? i.ItemType ?? 0;
        return itemType === 5 || itemType === 6;
      });

      const hasBatch = batchItems.length > 0;
      const hasSerial = serialItems.length > 0;

      return {
        category: 'ANBEFALT',
        name: 'Batch/Serial varer',
        status: (hasBatch || hasSerial) ? 'pass' : 'info',
        message: (hasBatch || hasSerial)
          ? `${batchItems.length} batch-vare(r), ${serialItems.length} serial-vare(r) funnet`
          : 'Ingen batch/serial varer funnet — batch/serial toggle vil ikke ha effekt',
        canProceed: true,
        canAutoFix: false
      };
    } catch (error) {
      return {
        category: 'ANBEFALT',
        name: 'Batch/Serial varer',
        status: 'info',
        message: 'Kunne ikke sjekke batch/serial varer',
        canProceed: true,
        canAutoFix: false
      };
    }
  }

  // ============================================================================
  // Auto-fix Functions
  // ============================================================================

  private async generateTestOrders(): Promise<void> {
    console.log('[PreFlightValidator] Generating test orders...');
    try {
      // Hent warehouse for ordre-generering
      await this.rateLimiter.throttle();
      const warehouses = await authenticatedGet(`${API_BASE_URL}/${this.companyNo}/warehouse`);
      const warehouseList = Array.isArray(warehouses) ? warehouses : [warehouses].filter(Boolean);
      const warehouseId = warehouseList.length > 0
        ? (warehouseList[0].warehouseID || warehouseList[0].WarehouseID)
        : null;

      for (let i = 0; i < 10; i++) {
        await this.rateLimiter.throttle();
        try {
          // Request body med påkrevde felter
          await authenticatedPost(`${API_BASE_URL}/orders/generate-random`, {
            numberOfLines: Math.floor(Math.random() * 5) + 1, // 1-5 linjer per ordre
            allowItemsWithoutStock: false, // Krever at varer har beholdning
            warehouse: warehouseId,
            maxQuantityPerLine: 10
          });
        } catch (error) {
          console.error(`Failed to generate order ${i + 1}:`, error);
        }
      }
      console.log('[PreFlightValidator] Test orders generated');
    } catch (error) {
      console.error('[PreFlightValidator] Failed to generate test orders:', error);
    }
  }

  private async generatePurchaseOrders(): Promise<void> {
    console.log('[PreFlightValidator] Generating purchase orders...');
    try {
      // Hent warehouse for ordre-generering
      await this.rateLimiter.throttle();
      const warehouses = await authenticatedGet(`${API_BASE_URL}/${this.companyNo}/warehouse`);
      const warehouseList = Array.isArray(warehouses) ? warehouses : [warehouses].filter(Boolean);
      const warehouseId = warehouseList.length > 0
        ? (warehouseList[0].warehouseID || warehouseList[0].WarehouseID)
        : null;

      for (let i = 0; i < 5; i++) {
        await this.rateLimiter.throttle();
        try {
          // Request body med påkrevde felter
          await authenticatedPost(`${API_BASE_URL}/porders/generate-random`, {
            numberOfLines: Math.floor(Math.random() * 5) + 2, // 2-6 linjer per ordre
            allowItemsWithoutStock: true, // Innkjøpsordrer trenger ikke eksisterende beholdning
            warehouse: warehouseId,
            maxQuantityPerLine: 50
          });
        } catch (error) {
          console.error(`Failed to generate purchase order ${i + 1}:`, error);
        }
      }
      console.log('[PreFlightValidator] Purchase orders generated');
    } catch (error) {
      console.error('[PreFlightValidator] Failed to generate purchase orders:', error);
    }
  }

  private async generateStockViaReceiving(): Promise<void> {
    console.log('[PreFlightValidator] Stock generation requires Receiving workflow - will be handled by simulator');
    // This is handled by the ReceivingSimulator workflow
  }

  private async createPickingListsViaRuleEngine(): Promise<void> {
    console.log('[PreFlightValidator] Creating picking lists via RuleEngine...');
    try {
      await this.rateLimiter.throttle();
      const setup = await authenticatedGet(`${API_BASE_URL}/system-setup/current`);

      await this.rateLimiter.throttle();
      const warehouses = await authenticatedGet(`${API_BASE_URL}/${this.companyNo}/warehouse`);
      const warehouseList = Array.isArray(warehouses) ? warehouses : [warehouses].filter(Boolean);

      if (warehouseList.length > 0 && setup) {
        const ruleGroupId = setup.stdRuleGroupID || setup.StdRuleGroupID;
        const warehouseId = warehouseList[0].warehouseID || warehouseList[0].WarehouseID;

        await this.rateLimiter.throttle();
        await authenticatedPost(`${API_BASE_URL}/${this.companyNo}/rulegroupengine/execute`, {
          ruleGroupID: ruleGroupId,
          companyNo: this.companyNo,
          warehouseID: warehouseId,
          sessionID: `SIM-PREFLIGHT-${Date.now()}`
        });

        console.log('[PreFlightValidator] Picking lists created via RuleEngine');
      }
    } catch (error) {
      console.error('[PreFlightValidator] Failed to create picking lists:', error);
    }
  }

  private async releaseAllLocks(lockedLists: any[]): Promise<void> {
    console.log(`[PreFlightValidator] Releasing ${lockedLists.length} locks...`);
    for (const list of lockedLists) {
      await this.rateLimiter.throttle();
      try {
        const pListId = list.pListId || list.PListId || list.pListID;
        const lockedBy = list.lockedBy || list.LockedBy;

        await authenticatedPost(`${API_BASE_URL}/picking/lists/${pListId}/unlock`, {
          operatorId: lockedBy
        });
      } catch (error) {
        console.error(`Failed to release lock on ${list.pListId}:`, error);
      }
    }
    console.log('[PreFlightValidator] All locks released');
  }

  // ============================================================================
  // Report Generation
  // ============================================================================

  private generateReport(validations: PreFlightValidation[]): PreFlightReport {
    const criticalErrors = validations.filter(v => v.category === 'KRITISK' && v.status === 'fail').length;
    const warnings = validations.filter(v => v.status === 'warning').length;
    const passed = validations.filter(v => v.status === 'pass').length;

    // Can only start if no critical errors and all validations that block can proceed
    const canStart = validations.every(v => v.canProceed);

    return {
      validations,
      canStart,
      criticalErrors,
      warnings,
      passed,
      timestamp: new Date()
    };
  }
}
